import { useState } from "react"
import InputItem from "../../components/InputItem"
import Loading from "../../components/Loading"
import CStack from "../../components/CStack"
import SwitchControl from "../../components/SwitchControl"
import { Stack, Button, Spinner } from "react-bootstrap"
import BaseControl from "../../components/BaseControl"
import { useFeatureStore } from "../../api/features/store"
import useUXStore from "../../api/ux/store"

const Features = () => {
    const { retrieveData, data, loading, updateData } = useFeatureStore(state => state)
    const addToast = useUXStore(state => state.addToast)
    const [edit, setEdit] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [editedData, setEditedData] = useState(data)
    const [applying, setApplying] = useState(false)

    if (!loading && data === null) retrieveData()
    if (data !== null && editedData === null) setEditedData(data)

    const onChangedValue = (key: string, val: any) => {
        const newData = {
            ...editedData,
            [key]: val
        }
        setEditedData(newData)
        setDirty(true)
    }

    const onToggleEdit = (checked: boolean) => {
        if (!checked) {
            setDirty(false)
            setEditedData(data)
        } 
        setEdit(checked)
    }


    const onApplyEdit = async () => {
        if (!editedData) return
        setApplying(true)
        try {
            await updateData(editedData)
            addToast("Features Saved", "Feature settings updated successfully.")
            setDirty(false)
            setEdit(false)
        } catch (error: any) {
            const message = error?.message ?? "Failed updating feature settings."
            addToast("Save Failed", message)
        } finally {
            setApplying(false)
        }
    }

    return <>
        {loading && <Loading expandHeight />}
        {data !== null && editedData !== null && <>
            <SwitchControl text="Edit Mode" value={edit} onChanged={onToggleEdit} />
            <SwitchControl edit={edit} text="Maintenance" caption="Make the mobile app inaccessible." value={editedData.UnderMaintenance} onChanged={val => onChangedValue("UnderMaintenance", val)} />

            <CStack header="Security">
                <InputItem edit={edit} type="number" inputSize={3} text="Device Limit" caption="Device limit per user." value={editedData.MaxDevices} id="MaxDevices" onChanged={(id, val) => onChangedValue(id, val)} />
                <InputItem edit={edit} type="number" inputSize={3} text="Login Retry Limit" caption="When login failure count reach, the user will be block." value={editedData.MaxFailedLoginRetry} id="MaxFailedLoginRetry" onChanged={(id, val) => onChangedValue(id, val)} />
                <InputItem edit={edit} type="number" inputSize={3} text="Session TTL" caption="Number of days that can reauthenticate session." value={editedData.SessionTTL} id="SessionTTL" onChanged={(id, val) => onChangedValue(id, val)} />
                <InputItem edit={edit} type="number" inputSize={3} text="Screen Idle" caption="Number of minutes the app becomes idle from inactivity." value={editedData.IdleTime} id="IdleTime" onChanged={(id, val) => onChangedValue(id, val)} />
            </CStack>

            <CStack header="Authentication">
                <SwitchControl edit={edit} text="MPIN" caption="Authorize transactions via nominated code." value={editedData.EnableMPIN} onChanged={val => onChangedValue("EnableMPIN", val)} />
                <SwitchControl edit={edit} text="OTP" caption="Authorize transactions with sms and email." value={editedData.EnableOTP} onChanged={val => onChangedValue("EnableOTP", val)} />
                {editedData.EnableOTP && 
                    <InputItem edit={edit} type="number" inputSize={3} text="OTP Expiration" caption="OTP Validity in minutes." value={editedData.OtpExpirationMinutes} onChanged={(id,val) => onChangedValue("OtpExpirationMinutes", val)} />}
                <InputItem edit={edit} text="OTP Disclaimer" caption="Displayable text prompt when OTP is turned on." value={editedData.OtpDisclaimer} id="OtpDisclaimer" onChanged={(id, val) => onChangedValue(id, val)} />
            </CStack>

            <CStack header="Services">
                <SwitchControl edit={edit} text="Bills Payment" value={editedData.EnableBillsPayment} onChanged={val => onChangedValue("EnableBillsPayment", val)} />
            </CStack>

            {edit && <BaseControl text="Apply changes?">
                <Stack direction="horizontal" gap={2}>
                    <Button size="sm" onClick={() => onToggleEdit(false)} variant="outline-secondary">Cancel</Button>
                    <Button size="sm" disabled={!dirty || applying} onClick={onApplyEdit} variant="primary">
                        {!applying ? "Apply" : <Stack direction="horizontal" gap={1}><Spinner size="sm" />Applying</Stack>}
                    </Button>
                </Stack>
            </BaseControl>}
        </>}
    </>
}

export default Features
